import { useCallback, useEffect, useRef, useState } from "react";
import type { ActionSurfaceCapabilities } from "../action-capabilities";
import type { UseActionAnalyticsResult } from "../use-action-analytics";
import { useActionLaunch } from "../use-action-launch";
import { useActionStore } from "../use-action-store";
import { ContextBrief } from "./context-brief";
import { ContextNotes } from "./context-notes";
import { ContextSuggestion } from "./context-suggestion";

interface ContextTabProps {
  capabilities: ActionSurfaceCapabilities;
  mentorshipId: string | null;
  analytics: UseActionAnalyticsResult;
}

export function ContextTab({
  capabilities,
  mentorshipId,
  analytics,
}: ContextTabProps) {
  const { suggestion, dismiss, launchSuggestion } = useActionLaunch();
  const [focusNoteId, setFocusNoteId] = useState<string | null>(null);

  const suggestionRef = useRef<HTMLElement>(null);
  const viewedKind = useRef<string | null>(null);

  const launchToken = useActionStore((s) => s.pendingLaunch);
  const clearPendingLaunch = useActionStore((s) => s.clearPendingLaunch);

  useEffect(() => {
    if (launchToken === null) return;
    suggestionRef.current?.scrollIntoView({
      behavior: "smooth",
      block: "start",
    });
    clearPendingLaunch();
  }, [launchToken, clearPendingLaunch]);

  useEffect(() => {
    if (!suggestion) {
      viewedKind.current = null;
      return;
    }
    if (viewedKind.current === suggestion.kind) return;
    viewedKind.current = suggestion.kind;
    analytics.trackSuggestionView(suggestion.kind);
  }, [suggestion, analytics]);

  const handleAccept = useCallback(() => {
    launchSuggestion();
  }, [launchSuggestion]);

  const handleDismiss = useCallback(() => {
    analytics.trackSuggestionDismiss(suggestion?.kind ?? null);
    dismiss();
  }, [analytics, dismiss, suggestion?.kind]);

  return (
    <div className="flex flex-col">
      {suggestion ? (
        <ContextSuggestion
          ref={suggestionRef}
          suggestion={suggestion}
          onAccept={handleAccept}
          onDismiss={handleDismiss}
        />
      ) : null}

      <ContextBrief mentorshipId={mentorshipId} />
      <ContextNotes
        mentorshipId={mentorshipId}
        capabilities={capabilities}
        analytics={analytics}
        focusNoteId={focusNoteId}
        onFocusNoteHandled={() => setFocusNoteId(null)}
      />
    </div>
  );
}
